import React, { useCallback,useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppButton, Box, ErrorModal, OptionKey, PlusIcon, Preloader, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import { SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import { Modal } from '@/components';

import FieldLevelAccessSetupForm from '../FieldLevelAccessSetup/FieldLevelAccessSetupForm';
import useCreateFieldLevelAccessQuery from '../queries/useCreateFieldLevelAccess.query';
import { IFieldLevelAccessSetup } from '../role.types';

import { useDefaultPropertiesValues } from './hooks/useDefaultPropertiesValues';

interface Props {
  setLevelAccessValue?: (value: OptionKey) => void;
  refetch?: () => void;
}
const FieldLevelAccessCreateModal: React.FC<Props> = ({ setLevelAccessValue, refetch }) => {
  const [visible, setVisible] = useState(false);
  const [currentState, setCurrentState] = useState<Partial<IFieldLevelAccessSetup> | null>(null);
  const [isServerError, setIsServerError] = useState(false);

  
  const { preparedProperties, preparedOverridableProperties, propLoading } = useDefaultPropertiesValues();

  const { handleSave, isLoading } = useCreateFieldLevelAccessQuery({
    onClose: () => {
      setVisible(false);
    },
    onError: () => {
      setIsServerError(true);
    },
  });

  const defaultValues = useMemo(() => {
    return {
      id: '',
      name: '',
      description: '',
      properties: preparedProperties,
      overridableProperties: preparedOverridableProperties,
      roles: [],
    };
  }, [preparedProperties, preparedOverridableProperties]);

  const handleSaveFieldLevelAccess = useCallback(async (data?: IFieldLevelAccessSetup) => {
    try {
      setIsServerError(false);
      if (currentState || data) {
        const response = await handleSave(currentState as IFieldLevelAccessSetup || data);
        const id = _.get(response, 'data.id');
        if (refetch) {
          await refetch();
        }
        if (setLevelAccessValue) {
          setLevelAccessValue(`${id}` as OptionKey);
        }
      }
    } catch {
      setIsServerError(true);
    }
  }, [currentState, handleSave, refetch, setLevelAccessValue]);

  const handleSubmit = useCallback(async (data: IFieldLevelAccessSetup) => {
    const preparedData = {
      name: data.name,
      description: data.description,
      properties: data.properties ,
      overridableProperties: data.overridableProperties,
    };
    setCurrentState(preparedData);
    await handleSaveFieldLevelAccess(preparedData as IFieldLevelAccessSetup);
    setVisible(false);
  } ,[handleSaveFieldLevelAccess]);



  const modalContent = (
    <>
      <Box style={{zIndex: 10000}}>
        <ErrorModal
          testId="FieldLevelAccessCreateModal_server-error"
          visible={isServerError}
          header={SERVER_ERROR_MODAL_TITLE}
          helpText={SERVER_ERROR_MODAL_TEXT}
          buttonText="Close"
          
          buttonTextTryAgain="Try again"
          onSetVisible={setIsServerError}
          onSubmit={handleSaveFieldLevelAccess}
        />
      </Box>
      <Modal
        testId='field-level-access-create-modal'
        visible={visible}
        onSetVisible={setVisible}
        wrapperStyle={{ zIndex: 10000 }}
      >
        <FieldLevelAccessSetupForm
          key="field-level-access-create-modal"
          defaultValues={defaultValues}
          isLoading={propLoading}
          onSubmit={handleSubmit}
          isModalMode
          isCreateMode
          title={(
            <Text size="2xl" weight={700} margin={{ bottom: 'l' }} style={{ lineHeight: '32px' }}>
              Create Field Level Access
            </Text>
          )}
          footer={(
            <Box margin={{top: 'l'}} direction="row" justify="end">
              <AppButton
                testId='field-level-access-create_cancel'
                width='160px'
                buttonType='secondary'
                onClick={() => setVisible(false)}
              >
                Cancel
              </AppButton>
              <Box margin={{left: 's'}}>
                <AppButton
                  testId='field-level-access-create'
                  width='160px' 
                  buttonType='primary'
                  type='submit'
                  disabled={isLoading}
                >
                  {isLoading ? <Preloader /> : 'Next (1/2)'}
                </AppButton>
              </Box>
            </Box>
          )}
        >
        </FieldLevelAccessSetupForm>
      </Modal>
    </>
  );

  return (
    <>
      {createPortal(
        modalContent,
        document.body
      )}
      <AppButton
        testId="field-level-access_add"
        onClick={() => setVisible(true)}
        buttonType="secondary"
        onlyIcon
        width="40px"
        style={{
          paddingInline: 0,
        }}
      >
        <PlusIcon size="16px" />
      </AppButton>
    </>
  );
};

export default FieldLevelAccessCreateModal;
